import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { PageHeader } from '~/components/data';
import { Button, Checkbox, Combobox } from '~/components/primitives';
import { warehouseAccessCandidates } from '~/lib/admin/warehouse-access';
import { fetchUserWarehouses, fetchUsers, setUserWarehouses } from '~/lib/api/admin';
import { fetchWarehouses } from '~/lib/api/inventory';
import { getRoleLabel } from '~/lib/rbac';
import { useActiveEntityStore } from '~/store/active-entity';
import styles from './_auth.admin.warehouse-access.module.css';

/**
 * B.7 — Admin /admin/warehouse-access: per-user warehouse assignment editor.
 *
 * Flow: select user → показать checkbox list warehouses → save → PUT /admin/users/:id/warehouses с
 * полным списком.
 *
 * Кого показывать — `warehouseAccessCandidates` (роль членства в активной компании; admin и manager
 * видят все склады — их в списке нет).
 */

export const Route = createFileRoute('/_auth/admin/warehouse-access')({
  component: WarehouseAccessAdmin,
});

function WarehouseAccessAdmin(): JSX.Element {
  const qc = useQueryClient();
  const activeEntityId = useActiveEntityStore((s) => s.activeEntityId);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => fetchUsers(),
  });

  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => fetchWarehouses(),
  });

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const assignmentsQuery = useQuery({
    queryKey: ['admin', 'user-warehouses', selectedUserId],
    queryFn: () => fetchUserWarehouses(selectedUserId),
    enabled: Boolean(selectedUserId),
  });

  useEffect(() => {
    if (assignmentsQuery.data) {
      setChecked(new Set(assignmentsQuery.data.assignments.map((a) => a.warehouse_id)));
    }
  }, [assignmentsQuery.data]);

  const saveMut = useMutation({
    mutationFn: () => setUserWarehouses(selectedUserId, Array.from(checked)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user-warehouses', selectedUserId] });
    },
  });

  // С-I7 Р.3 (ACC-B40, ACC-F8): список и роль — по членству в активной компании, не по `users.role`.
  const candidates = warehouseAccessCandidates(usersQuery.data?.items ?? [], activeEntityId);

  const warehouses = warehousesQuery.data?.items ?? [];

  return (
    <div className={styles.page}>
      <PageHeader title={m.route_admin_warehouse_access_title()} />
      {/* С-I7, круг 1 гейта (UI-3, UI-4, UX-5): выбор человека — тем же видом, что выбор согласующего
          (имя и роль, почта строкой ниже, поиск), без пункта-заглушки «—»; почему в списке нет админов —
          сказано подсказкой. */}
      <Combobox
        className={styles.userPicker}
        label={m.route_admin_warehouse_access_user_label()}
        value={selectedUserId}
        onChange={setSelectedUserId}
        placeholder={m.route_admin_warehouse_access_user_placeholder()}
        hint={m.route_admin_warehouse_access_unrestricted_hint()}
        noResultsText={m.route_admin_warehouse_access_user_not_found()}
        options={candidates.map((u) => ({
          value: u.id,
          label: m.route_admin_warehouse_access_user_option({
            name: u.fullName ?? u.email,
            role: getRoleLabel(u.role),
          }),
          ...(u.fullName ? { hint: u.email } : {}),
        }))}
      />

      {selectedUserId && (
        <>
          <div className={styles.grid}>
            {warehouses.map((w) => (
              <Checkbox
                key={w.id}
                checked={checked.has(w.id)}
                onChange={(e) => {
                  const next = new Set(checked);
                  if (e.target.checked) next.add(w.id);
                  else next.delete(w.id);
                  setChecked(next);
                }}
                label={
                  <>
                    <span className={styles.code}>{w.code}</span>{' '}
                    <span className={styles.subtle}>{w.name}</span>
                  </>
                }
              />
            ))}
          </div>
          <div className={styles.actions}>
            <Button variant="primary" onClick={() => saveMut.mutate()} loading={saveMut.isPending}>
              <Save size={16} /> {m.common_button_save()}
            </Button>
            <span className={styles.count}>
              {m.route_admin_warehouse_access_count({ count: checked.size })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
